import { useEffect, useState } from 'react'
import { fetchLatestTestRun } from '../api/hardware-tests.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { HardwareTestResultModel } from '../models'

import { usePortalQuery } from 'src/Libs/api'

type UsePendingTestsReturnType = [HardwareTestResultModel | undefined, boolean, boolean, () => void]

/**
 * Hook to get the latest test run result, if that has available tests, meaning the test cycle has not been completed yet.
 * @param serialNumber
 */
export const usePendingTests = (storageId: string | undefined): UsePendingTestsReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const [data, setData] = useState<HardwareTestResultModel | undefined>(undefined)

  const fetchLatestTestRunQueryFn = async (): Promise<HardwareTestResultModel | undefined> => {
    const { error, hasErrors, value } = await fetchLatestTestRun(storageId, authFetch)

    if (hasErrors) {
      throw error || new Error('An unexpected error occurred when fetching latest test run')
    }

    if (value?.availableTests?.length) {
      return value
    }

    return undefined
  }

  const resetPendingTestData = (): void => {
    setData(undefined)
  }

  const {
    data: requestData,
    isLoading,
    isError,
  } = usePortalQuery({
    queryKey: ['hardware-tests', 'storages', storageId, 'tests', 'runs', 'latest'],
    queryFn: fetchLatestTestRunQueryFn,
    enabled: !!storageId,
  })

  useEffect(() => {
    setData(requestData ?? undefined)
  }, [requestData])

  return [data, isLoading, isError, resetPendingTestData]
}
